import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  /** @type {IntersectionObserver | null} */
  #autoplayObserver = null;

  /** @type {boolean} Whether the browser has presented a real frame in place of the poster. */
  #firstFramePresented = false;

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    // The gallery's slideshow plays and pauses its slides' videos directly (assets/slideshow.js), so
    // follow the video's own state to keep the control right. Neither event bubbles.
    for (const type of ['play', 'pause']) {
      this.addEventListener(
        type,
        (event) => {
          if (!(event.target instanceof HTMLVideoElement)) return;
          this.isPlaying = !event.target.paused;
          this.updatePlayPauseHint(this.isPlaying);
        },
        { signal, capture: true },
      );
    }

    // A non-looping video that reaches its end is no longer playing, so keep the control in sync.
    // 'ended' does not bubble; capture still reaches us on the way down to the <video>.
    this.addEventListener(
      'ended',
      () => {
        this.isPlaying = false;
        this.updatePlayPauseHint(false);
      },
      { signal, capture: true },
    );

    if (this.hasAttribute('data-autoplay-on-visible')) {
      // Priming (see #setupAutoplayOnVisible) plays adjacent carousel slides via a 100% rootMargin,
      // and play() forces the video to download even with preload="none". Hold that setup until the
      // page has loaded so a gallery video never competes with the main image in the LCP window;
      // once connected after load (e.g. a variant re-render) it primes immediately as before.
      if (document.readyState === 'complete') {
        this.#setupAutoplayOnVisible();
      } else {
        window.addEventListener('load', () => this.#setupAutoplayOnVisible(), { once: true, signal });
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#autoplayObserver?.disconnect();
    this.#autoplayObserver = null;
  }

  /**
   * Plays the media while it is in the viewport and pauses it once it leaves.
   * The media is already in the DOM, so this only toggles playback.
   */
  #setupAutoplayOnVisible() {
    const video = this.querySelector('video');

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      // Cancel the markup's native autoplay and hold the poster frame. The play/pause control
      // still starts it on demand.
      video?.removeAttribute('autoplay');
      video?.pause();
      this.isPlaying = false;
      this.updatePlayPauseHint(false);
      return;
    }

    this.#watchForFirstFrame(video);

    if (!('IntersectionObserver' in window)) return;

    this.#autoplayObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.playMedia();
          } else if (this.#firstFramePresented) {
            // Pausing before the first frame lands would cancel the autoplay that primes it,
            // putting the poster-to-frame flicker back on the first swipe.
            this.pauseMedia();
          }
        }
      },
      // Generous margin so a slide adjacent to the viewport is already primed and playing by
      // the time it is swiped into view.
      { threshold: 0.25, rootMargin: '100%' },
    );

    this.#autoplayObserver.observe(this);

    // The shared handler above pauses every deferred-media when the zoom dialog closes, and the
    // observer is edge-triggered so it won't fire again on its own. Re-observing makes it
    // re-report the current state, resuming a video that is still on screen.
    window.addEventListener(DialogCloseEvent.eventName, () => this.#reportVisibilityAgain(), {
      signal: this.#abortController.signal,
    });

    // Native autoplay used to prime the first frame, and the markup no longer sets it so the file
    // cannot download during the LCP window. The observer above cannot inherit that job: the
    // carousel clips off-screen slides, so their intersection rect is empty and no rootMargin —
    // not even 100% — makes them report as intersecting. Buffering alone would not help either,
    // because a paused video keeps its show-poster flag until playback or a seek actually happens.
    // So prime with a real play; #watchForFirstFrame pauses it again the moment a frame lands.
    if (!this.#firstFramePresented) this.playMedia();
  }

  /**
   * Marks the element as primed once a real frame replaces the poster, then lets the observer
   * re-decide playback — an off-screen video that was only playing in order to prime gets paused.
   * @param {HTMLVideoElement | null} video
   */
  #watchForFirstFrame(video) {
    if (!video) {
      this.#firstFramePresented = true;
      return;
    }

    const onFirstFrame = () => {
      this.#firstFramePresented = true;
      this.#reportVisibilityAgain();
    };

    if ('requestVideoFrameCallback' in video) {
      // The only signal that a frame has actually been painted.
      video.requestVideoFrameCallback(onFirstFrame);
    } else {
      // readyState >= HAVE_CURRENT_DATA: a frame is decoded and about to be shown.
      video.addEventListener('loadeddata', onFirstFrame, {
        once: true,
        signal: this.#abortController.signal,
      });
    }
  }

  /**
   * IntersectionObserver is edge-triggered. Re-observing forces it to report the current state
   * again so playback can be re-decided without duplicating the decision logic.
   */
  #reportVisibilityAgain() {
    this.#autoplayObserver?.unobserve(this);
    this.#autoplayObserver?.observe(this);
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
  }

  /**
   * Toggle play/pause state of the media
   * @param {Event} [event]
   */
  toggleMedia(event) {
    // Keep the tap off the gallery's slide/zoom handlers.
    event?.stopPropagation?.();

    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*',
      );
    } else {
      // Scrolling fast enough to play then immediately pause rejects this promise; ignore it.
      this.querySelector('video')
        ?.play()
        .catch(() => {});
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*',
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal },
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal },
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
